import { useCallback, useEffect, useRef, useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import { invalidatePlaybackInfo } from '../../../hooks/usePlaybackUrl';
import type {
  VideoAsset,
  VideoMetadata,
} from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { useVideoLibraryPolling } from './useVideoLibraryPolling';
import { useVideoLibraryUpload } from './useVideoLibraryUpload';
import { errText } from './videoLibraryErrorText';

/**
 * The video library: search, upload (compression runs server-side after a
 * presigned-to-GCS upload), edit metadata, and delete. Polls while any video is
 * still PENDING/PROCESSING so the admin sees compression finish without reloading.
 */
export function useVideoLibrary(opts: {
  fundOpportunityId: string;
  backendService: BackendService;
}) {
  const { fundOpportunityId, backendService } = opts;
  const [videos, setVideos] = useState<VideoAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const searchRef = useRef(search);
  searchRef.current = search;

  const refresh = useCallback(async () => {
    try {
      const results = await backendService.listVideoLibrary({
        title: searchRef.current || undefined,
      });
      setVideos(results);
      setError(null);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  }, [backendService]);

  useEffect(() => {
    setLoading(true);
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const isProcessing = useVideoLibraryPolling(videos, refresh);
  const { uploading, uploadError, upload, dismissUploadError } = useVideoLibraryUpload(
    backendService,
    fundOpportunityId,
    refresh,
  );

  /** Saves edited metadata and drops any cached playback text so open players pick it up. */
  const updateMetadata = async (externalId: string, fields: Partial<VideoMetadata>) => {
    await backendService.updateVideoMetadata(externalId, fields);
    invalidatePlaybackInfo(externalId);
    await refresh();
  };

  const remove = async (externalId: string, force: boolean) => {
    await backendService.deleteVideoAsset(externalId, force);
    await refresh();
  };

  return {
    videos,
    loading,
    error,
    search,
    setSearch,
    uploading,
    uploadError,
    dismissUploadError,
    isProcessing,
    upload,
    updateMetadata,
    remove,
  };
}
